import { useCallback, useEffect, useState } from 'react'

/**
 * Die Reiter der App. Die Reihenfolge bestimmt auch die Anzeige.
 *
 * 'druck' steht bewusst am Ende und wird in der Reiterleiste nicht mitgezählt,
 * sondern als eigener Knopf rechts gesetzt (siehe App.tsx). Er gehört trotzdem
 * in diese Liste: nur was hier steht, überlebt ein Neuladen im URL-Fragment.
 */
export const TABS = [
  'etappen',
  'karte',
  'tagebuch',
  'budget',
  'checkliste',
  'reise',
  'daten',
  'druck',
] as const

export type Tab = (typeof TABS)[number]

const DEFAULT_TAB: Tab = 'etappen'

function fromHash(): Tab {
  const value = window.location.hash.replace(/^#\/?/, '') as Tab
  return TABS.includes(value) ? value : DEFAULT_TAB
}

/**
 * Hält den aktiven Reiter im URL-Fragment.
 *
 * Damit funktionieren Zurück-Taste, Neuladen und ein Lesezeichen auf das
 * Tagebuch, ohne dass ein Router mit Serverpfaden nötig wäre; der einzige
 * echte Pfad der App bleibt der Rückweg der Anmeldung.
 */
export function useTab(): [Tab, (tab: Tab) => void] {
  const [tab, setTab] = useState<Tab>(fromHash)

  useEffect(() => {
    const onHashChange = () => setTab(fromHash())
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, [])

  const select = useCallback((next: Tab) => {
    window.location.hash = `#/${next}`
    setTab(next)
  }, [])

  return [tab, select]
}
